import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useInfiniteQuery, useQueryClient } from 'react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { getImageRelationsByCategory, getImageRelationsByGroup, getImageRelationsByCollectionId } from '../api/image_relations';
import { getCollectionById, getCollectionCategoriesGrouped } from '../api/collections';
import { getCacheVersions } from '../api/cache';
import Spinner from "../components/Spinner";
import AppHeaderLayout from "../components/AppHeaderLayout";
import GalleryCard from '../components/Channel/GalleryCard';
import { useTranslation } from 'react-i18next';
import { iconButtonStyles } from '../styles/commonStyles';
import { BiMenu } from 'react-icons/bi';
import { CategoryScroller } from '../components/Channel/CategoryScroller';
import { groupCategoryButtonStyles } from '../styles/commonStyles';
import { getSavedLanguage } from '../utils/themeStorage';
import { useUserLevel } from '../hooks/useUserLevel';
import { useAuthStatus } from '../hooks/useAuthStatus';


// ID канала для галереи (фиксированный)
const GALLERY_CHANNEL_ID = 9;

// Grid constants for virtualization
const CARD_W = 80;
const CARD_H = 110;
const GAP = 8;

function GalleryCategoriesGroups({ groupedCategories, loading, selectedCategoryId, selectedGroupId, onCategorySelect, onGroupSelect }) {
	const navigate = useNavigate();
	const location = useLocation();

	// Безопасно извлекаем данные
	const groups = groupedCategories?.groups || [];
	const categoriesWithoutGroup = groupedCategories?.categories_without_group || [];

	function sortGroups(arr) {
		return [...arr].sort((a, b) => {
			// Sort by dynamic_score desc (server-computed relevance)
			const scoreA = a.group.dynamic_score ?? 0;
			const scoreB = b.group.dynamic_score ?? 0;
			if (scoreA !== scoreB) return scoreB - scoreA;
			// Fallback to position
			if (a.group.position != null && b.group.position != null) return a.group.position - b.group.position;
			if (a.group.position != null) return -1;
			if (b.group.position != null) return 1;
			return a.group.id - b.group.id;
		});
	}

	const sortedGroups = sortGroups(groups);
	const ALL_GROUP = '__all__';
	const [activeGroup, setActiveGroup] = useState(ALL_GROUP);

	// Определяем активную группу на основе выбранной категории или группы
	React.useEffect(() => {
		if (selectedGroupId && groups.length > 0) {
			// Если выбрана группа, устанавливаем её как активную
			setActiveGroup(selectedGroupId);
		} else if (selectedCategoryId && groups.length > 0) {
			// Ищем группу, которая содержит выбранную категорию
			const groupWithCategory = groups.find(g => 
				(g.categories || []).some(c => c.id == selectedCategoryId)
			);
			if (groupWithCategory) {
				setActiveGroup(groupWithCategory.group.id);
			} else {
				setActiveGroup(ALL_GROUP);
			}
		} else {
			setActiveGroup(ALL_GROUP);
		}
	}, [selectedCategoryId, selectedGroupId, groups]);

	if (loading) {
		return <div className="mb-4 text-gray-500">Загрузка категорий...</div>;
	}

	if (!groupedCategories) {
		return null;
	}
	
	if (groups.length === 0 && categoriesWithoutGroup.length === 0) {
		return null;
	}

	const allCategories = [
		...groups.flatMap(g => g.categories || []),
		...categoriesWithoutGroup
	];

	function sortCategories(arr) {
		return [...arr].sort((a, b) => {
			// Sort by dynamic_score desc (server-computed relevance)
			const scoreA = a.dynamic_score ?? 0;
			const scoreB = b.dynamic_score ?? 0;
			if (scoreA !== scoreB) return scoreB - scoreA;
			// Fallback to position
			if (a.position != null && b.position != null) return a.position - b.position;
			if (a.position != null) return -1;
			if (b.position != null) return 1;
			return a.id - b.id;
		});
	}

	if (groups.length > 0) {
		let categoriesToShow = [];
		if (activeGroup === ALL_GROUP) {
			categoriesToShow = sortCategories([
				...groups.flatMap(g => g.categories || []),
				...categoriesWithoutGroup
			]).slice(0, 20);
		} else {
			const group = groups.find(g => g.group.id === activeGroup);
			categoriesToShow = group ? sortCategories(group.categories || []) : [];
		}

		const handleCategorySelect = (catId) => {
			onCategorySelect(catId);
			// Убираем вызов onGroupSelect(null) чтобы избежать двойной навигации
		};

		return (
			<div className="mb-2 w-full overflow-x-hidden px-2 sm:px-0">
				<div className="flex flex-row gap-2 overflow-x-auto whitespace-nowrap w-full">
					<button
						key={ALL_GROUP}
						onClick={() => {
							setActiveGroup(ALL_GROUP);
							onCategorySelect(null);
							onGroupSelect(null);
						}}
						className={[
							'flex-shrink-0',
							groupCategoryButtonStyles,
							activeGroup === ALL_GROUP
								? 'bg-blue-500 text-white dark:bg-blue-400 dark:text-white'
								: ''
						].join(' ')}
						style={{ userSelect: 'none' }}
					>
						Все
					</button>
					{sortedGroups.map(({ group }) => (
						<button
							key={group.id}
							onClick={() => {
								setActiveGroup(group.id);
								onGroupSelect(group.id);
							}}
							className={[
								'flex-shrink-0',
								groupCategoryButtonStyles,
								activeGroup === group.id
									? 'bg-blue-500 text-white dark:bg-blue-400 dark:text-white'
									: ''
							].join(' ')}
							style={{ userSelect: 'none' }}
						>
							{group.name}
						</button>
					))}
				</div>
				<CategoryScroller
					categories={categoriesToShow}
					value={selectedCategoryId}
					onSelect={handleCategorySelect}
				/>
			</div>
		);
	}

	return (
		<div className="mb-4 px-2 sm:px-0">
			<CategoryScroller
				categories={sortCategories(allCategories)}
				value={selectedCategoryId}
				onSelect={onCategorySelect}
			/>
		</div>
	);
}

function Gallery({ sortOption, setSortOption }) {
	const location = useLocation();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { hasFeature } = useUserLevel();
	const [sidebarMenuOpen, setSidebarMenuOpen] = useState(false);

	const params = new URLSearchParams(location.search);
	const initialCategoryId = params.get('category') ? parseInt(params.get('category')) : null;
	const initialGroupId = params.get('group') ? parseInt(params.get('group')) : null;
	const filterNew = params.get('filter') === 'new';

	// Состояние для фильтров
	const [selectedCategoryId, setSelectedCategoryId] = useState(initialCategoryId);
	const [selectedGroupId, setSelectedGroupId] = useState(initialGroupId);

	// Отслеживание «новых» открыток
	const lastSeenRef = useRef(localStorage.getItem('agr_last_seen_gallery') || '');
	useEffect(() => {
		// Обновляем last_seen через 3 секунды после открытия галереи
		const timer = setTimeout(() => {
			localStorage.setItem('agr_last_seen_gallery', new Date().toISOString());
		}, 3000);
		return () => clearTimeout(timer);
	}, []);
	const [selectedLanguage, setSelectedLanguage] = useState(() => {
		// Загружаем сохраненный язык при инициализации
		const savedLanguage = getSavedLanguage();
		return savedLanguage === 'ru' || savedLanguage === 'en' ? savedLanguage : 'all';
	});

	// Обновляем состояние при изменении URL
	useEffect(() => {
		setSelectedCategoryId(initialCategoryId);
		setSelectedGroupId(initialGroupId);
	}, [initialCategoryId, initialGroupId]);

	// Синхронизация языка при навигации
	useEffect(() => {
		const savedLanguage = getSavedLanguage();
		if (savedLanguage !== selectedLanguage) {
			setSelectedLanguage(savedLanguage);
		}
	}, [location.pathname]);

	const handleCardClick = (postcardId) => {
		// Создаем query параметры для передачи контекста галереи в ShortsViewer
		const queryParams = new URLSearchParams();
		if (selectedCategoryId) {
			queryParams.set('category', selectedCategoryId.toString());
		}
		if (selectedGroupId) {
			queryParams.set('group', selectedGroupId.toString());
		}
		if (selectedLanguage && selectedLanguage !== 'all') {
			queryParams.set('language', selectedLanguage);
		}
		queryParams.set('from', 'gallery');
		
		const queryString = queryParams.toString();
		const url = `/mychannels/${GALLERY_CHANNEL_ID}/${postcardId}${queryString ? `?${queryString}` : ''}`;
		
		console.log('🔗 Navigating to ShortsViewer with context:', { 
			postcardId, 
			selectedCategoryId, 
			selectedGroupId, 
			url 
		});
		
		navigate(url);
	};

	const handleCategorySelect = (categoryId) => {
		console.log('🔍 handleCategorySelect called with:', categoryId);
		if (categoryId) {
			console.log('🚀 Navigating to:', `/gallery?category=${categoryId}`);
			setSelectedCategoryId(categoryId);
			setSelectedGroupId(null);
			navigate(`/gallery?category=${categoryId}`);
		} else {
			console.log('🚀 Navigating to:', '/gallery');
			setSelectedCategoryId(null);
			setSelectedGroupId(null);
			navigate('/gallery');
		}
	};

	const handleGroupSelect = (groupId) => {
		console.log('🔍 handleGroupSelect called with:', groupId);
		if (groupId) {
			console.log('🚀 Navigating to:', `/gallery?group=${groupId}`);
			setSelectedGroupId(groupId);
			setSelectedCategoryId(null);
			navigate(`/gallery?group=${groupId}`);
		} else {
			console.log('🚀 Navigating to:', '/gallery');
			setSelectedGroupId(null);
			setSelectedCategoryId(null);
			navigate('/gallery');
		}
	};

	const leftContent = (
		<div className="flex items-center gap-2">
			<button 
				className={iconButtonStyles + " block sm:hidden mr-2"} 
				onClick={() => setSidebarMenuOpen(true)} 
				aria-label="Открыть меню"
			>
				<BiMenu className="text-2xl" />
			</button>
			<span className="font-semibold text-base">{t('tabs.gallery') || 'Gallery'}</span>
		</div>
	);

	const rightContent = null;

	const { isReady } = useAuthStatus();
	const queryClient = useQueryClient();

	// Version-based cache invalidation for gallery
	useEffect(() => {
		if (!isReady) return;
		const checkVersion = async () => {
			try {
				const versions = await getCacheVersions();
				const serverVersion = versions?.gallery ?? -1;
				const storedVersion = parseInt(localStorage.getItem('agr_cache_gallery_version') || '-1');

				if (storedVersion !== -1 && storedVersion !== serverVersion) {
					// Gallery changed — invalidate all gallery queries
					queryClient.invalidateQueries(['gallery-postcards']);
					queryClient.invalidateQueries(['gallery-collection']);
					queryClient.invalidateQueries(['collectionCategoriesGrouped']);
				}
				localStorage.setItem('agr_cache_gallery_version', String(serverVersion));
			} catch {
				// Version check failed — no action needed
			}
		};
		checkVersion();
	}, [isReady, queryClient]);
	
	// React Query для загрузки данных с кешированием
	const { data: channelData, isLoading: isLoadingCollection, error: collectionError } = useQuery(
		['gallery-collection', GALLERY_CHANNEL_ID],
		() => getCollectionById(GALLERY_CHANNEL_ID),
		{
			staleTime: 5 * 60 * 1000, // 5 минут
			cacheTime: 10 * 60 * 1000, // 10 минут
			enabled: isReady, // Ждем готовности авторизации
		}
	);

	const { data: groupedCategories, isLoading: isLoadingCategories } = useQuery(
		['collectionCategoriesGrouped', GALLERY_CHANNEL_ID],
		() => getCollectionCategoriesGrouped(GALLERY_CHANNEL_ID),
		{
			staleTime: 5 * 60 * 1000, // 5 минут
			cacheTime: 10 * 60 * 1000, // 10 минут
			enabled: isReady, // Ждем готовности авторизации
		}
	);

	// Infinite scroll для посткардов
	const PAGE_SIZE = 40;
	const {
		data: postcardPages,
		isLoading: loadingPostcards,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery(
		['gallery-postcards', GALLERY_CHANNEL_ID, selectedCategoryId, selectedGroupId, selectedLanguage],
		async ({ pageParam = 0 }) => {
			const opts = { limit: PAGE_SIZE, offset: pageParam };
			let result;
			if (selectedCategoryId) {
				result = await getImageRelationsByCategory(GALLERY_CHANNEL_ID, selectedCategoryId, false, selectedLanguage, opts);
			} else if (selectedGroupId) {
				result = await getImageRelationsByGroup(GALLERY_CHANNEL_ID, selectedGroupId, false, selectedLanguage, opts);
			} else {
				result = await getImageRelationsByCollectionId(GALLERY_CHANNEL_ID, selectedLanguage, opts);
			}
			console.log(`📦 Gallery loaded page offset=${pageParam}: ${result?.length} items, first_id=${result?.[0]?.id}`);
			return result;
		},
		{
			getNextPageParam: (lastPage, allPages) => {
				if (!lastPage || lastPage.length < PAGE_SIZE) return undefined;
				return allPages.reduce((sum, p) => sum + p.length, 0);
			},
			staleTime: 0, // Всегда рефетч при навигации
			cacheTime: 5 * 60 * 1000,
			enabled: !!GALLERY_CHANNEL_ID && isReady,
		}
	);

	const postcards = postcardPages?.pages?.flat() || [];
	const hasPostcards = postcards.length > 0;

	// --- Virtualization setup ---
	const gridContainerRef = useRef(null);
	const [scrollEl, setScrollEl] = useState(null);
	const [columns, setColumns] = useState(4);

	// Find scroll parent (overflow-y: auto div from AppHeaderLayout)
	useEffect(() => {
		const el = gridContainerRef.current;
		if (!el) return;
		let parent = el.parentElement;
		while (parent) {
			const style = getComputedStyle(parent);
			if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
				setScrollEl(parent);
				return;
			}
			parent = parent.parentElement;
		}
	}, [hasPostcards]);

	// Measure container width for column count
	useEffect(() => {
		const el = gridContainerRef.current;
		if (!el) return;
		const observer = new ResizeObserver(([entry]) => {
			const w = entry.contentRect.width;
			setColumns(Math.max(1, Math.floor((w + GAP) / (CARD_W + GAP))));
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [hasPostcards]);

	const rowCount = Math.ceil(postcards.length / columns);

	const rowVirtualizer = useVirtualizer({
		count: rowCount,
		getScrollElement: () => scrollEl,
		estimateSize: () => CARD_H + GAP,
		overscan: 5,
		scrollMargin: gridContainerRef.current?.offsetTop ?? 0,
	});

	// Load more when scrolling near the end
	const virtualItems = rowVirtualizer.getVirtualItems();
	useEffect(() => {
		if (!hasNextPage || isFetchingNextPage || virtualItems.length === 0) return;
		const lastItem = virtualItems[virtualItems.length - 1];
		if (lastItem && lastItem.index >= rowCount - 3) {
			fetchNextPage();
		}
	}, [virtualItems, rowCount, hasNextPage, isFetchingNextPage, fetchNextPage]);

	if (isLoadingCollection || isLoadingCategories) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner />
			</div>
		);
	}

	if (collectionError) {
		return <div>Error loading channel data</div>;
	}

	return (
		<AppHeaderLayout
			leftContent={leftContent}
			rightContent={rightContent}
			sidebarMenuOpen={sidebarMenuOpen}
			onSidebarMenuOpen={() => setSidebarMenuOpen(true)}
			onSidebarMenuClose={() => setSidebarMenuOpen(false)}
			title={t('tabs.gallery') || 'Gallery'}
		>
			<div className="p-0 sm:p-4">
				{filterNew && (
					<div className="px-3 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400">
						Новые открытки
					</div>
				)}
				<div className="pt-2.5">
					<GalleryCategoriesGroups 
						groupedCategories={groupedCategories} 
						loading={isLoadingCategories}
						selectedCategoryId={selectedCategoryId}
						selectedGroupId={selectedGroupId}
						onCategorySelect={handleCategorySelect}
						onGroupSelect={handleGroupSelect}
					/>
				</div>

				{loadingPostcards ? (
					<Spinner />
				) : postcards.length === 0 ? (
					<div className="text-gray-500 dark:text-gray-400 px-4 sm:px-0 text-center py-8">
						{selectedCategoryId ? (
							<div>
								<div className="text-lg font-semibold mb-2">В категории нет открыток</div>
								<div className="text-sm">Попробуйте выбрать другую категорию</div>
							</div>
						) : selectedGroupId ? (
							<div>
								<div className="text-lg font-semibold mb-2">В группе нет открыток</div>
								<div className="text-sm">Попробуйте выбрать другую группу</div>
							</div>
						) : null}
					</div>
				) : (
					<div
						ref={gridContainerRef}
						className="px-2 sm:px-0"
						style={{
							height: `${rowVirtualizer.getTotalSize()}px`,
							position: 'relative',
						}}
					>
						{virtualItems.map(virtualRow => {
							const startIdx = virtualRow.index * columns;
							const rowCards = postcards.slice(startIdx, startIdx + columns);
							const scrollMargin = rowVirtualizer.options.scrollMargin;
							return (
								<div
									key={virtualRow.key}
									style={{
										position: 'absolute',
										top: 0,
										left: 0,
										right: 0,
										height: `${CARD_H}px`,
										transform: `translateY(${virtualRow.start - scrollMargin}px)`,
										display: 'flex',
										gap: `${GAP}px`,
										justifyContent: 'center',
									}}
								>
									{rowCards.map(postcard => {
										const isNew = lastSeenRef.current && postcard.created_at && new Date(postcard.created_at) > new Date(lastSeenRef.current);
										return (
										<GalleryCard
											key={postcard.id}
											postcard={postcard}
											onClick={() => handleCardClick(postcard.id)}
											className="w-[80px] h-[110px]"
											isNew={isNew}
										/>
										);
									})}
								</div>
							);
						})}
					</div>
				)}
				{isFetchingNextPage && <Spinner />}
			</div>
		</AppHeaderLayout>
	);
}


export default Gallery;